import { FaroText, FontWeights, grey } from "@faro-lotv/flat-ui";
import { Box, ButtonBase } from "@mui/material";
import {
  useEffect,
  useMemo,
  useRef,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { RangeRole, cellColors } from "../calendar/calendar-styles";
import { captureLabel, releaseTouchCapture } from "../calendar/calendar-utils";
import { ViewProps } from "../calendar/capture-calendar-types";
import { CaptureCountChip } from "../calendar/capture-markers";
import { Cell } from "../range-selection";
import { TimelineRail } from "./timeline-rail";
import { timelineRoles, timelineRows } from "./timeline-rows";

const LIST_MAX_HEIGHT = "240px";
const LIST_ROW_RADIUS = "4px";

/** Width of the date column, so the weekdays line up */
const LIST_DATE_WIDTH = "72px";

/** Height of the bands at the list's top and bottom where a resting drag scrolls it */
const SCROLL_EDGE_PX = 32;
/** How far the list scrolls per frame while a drag rests in or beyond those bands */
const SCROLL_STEP_PX = 4;

/** How long a finger must rest on a row before it drags a range rather than scrolls */
const TOUCH_HOLD_MS = 300;

/**
 * @returns A timeline of the days holding captures, newest first, each month opening with a
 * row that picks it whole
 */
export function CaptureDateList({
  stats,
  startDate,
  endDate,
  onPick,
  onHover,
  onPress,
  isDragging,
  isPickable,
}: ViewProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  const rows = useMemo(() => timelineRows(stats.days), [stats.days]);
  const roles = timelineRoles(rows, startDate, endDate);
  const touchHold = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Set as soon as a held finger starts a drag, before React re-renders with isDragging
  const isTouchDragging = useRef(false);

  // A finger dragging a range must not also scroll the list; needs a non-passive listener
  useEffect(() => {
    const list = listRef.current;
    function holdScroll(event: TouchEvent): void {
      if (isTouchDragging.current) event.preventDefault();
    }
    list?.addEventListener("touchmove", holdScroll, { passive: false });
    return () => {
      list?.removeEventListener("touchmove", holdScroll);
      clearTimeout(touchHold.current);
    };
  }, []);

  // A mouse or pen drags at once; a finger only once held still, so a swipe still scrolls
  function pressRow(event: ReactPointerEvent, cell: Cell): void {
    if (event.pointerType !== "touch") {
      onPress?.(cell);
      return;
    }
    releaseTouchCapture(event);
    clearTimeout(touchHold.current);
    touchHold.current = setTimeout(() => {
      isTouchDragging.current = true;
      onPress?.(cell);
    }, TOUCH_HOLD_MS);
  }

  // Scrolls while a drag rests near the list's top or bottom, or beyond them
  useEffect(() => {
    if (!isDragging) return;
    let direction = 0;
    function aim({ clientY }: PointerEvent): void {
      const list = listRef.current;
      if (!list) return;
      const { top, bottom } = list.getBoundingClientRect();
      direction =
        Number(clientY > bottom - SCROLL_EDGE_PX) -
        Number(clientY < top + SCROLL_EDGE_PX);
    }
    let frame = requestAnimationFrame(function scroll() {
      const list = listRef.current;
      if (list) list.scrollTop += direction * SCROLL_STEP_PX;
      frame = requestAnimationFrame(scroll);
    });
    // Capture phase, as EventBarrier stops pointer moves from bubbling up to the window
    window.addEventListener("pointermove", aim, { capture: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", aim, { capture: true });
      isTouchDragging.current = false;
    };
  }, [isDragging]);

  // Open on the selection rather than on the most recent captures
  useEffect(() => {
    const list = listRef.current;
    const selected = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (list && selected) {
      list.scrollTop = selected.offsetTop - list.clientHeight / 2;
    }
  }, []);

  return (
    <Box
      ref={listRef}
      component="div"
      // A tap, or a swipe the browser turned into a scroll, ends the hold before it drags
      onPointerUp={() => clearTimeout(touchHold.current)}
      onPointerCancel={() => clearTimeout(touchHold.current)}
      // A held finger would otherwise open the browser's context menu mid-drag
      onContextMenu={(event) => event.preventDefault()}
      sx={{
        maxHeight: LIST_MAX_HEIGHT,
        overflowY: "auto",
        // Keep the scrollbar's space so it never covers the counts when it appears
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: `${grey[700]} transparent`,
        userSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >
      {rows.map(({ date, count, isMonth }, index) => {
        const role = roles[index];
        const isEndpoint = role.isRangeStart || role.isRangeEnd;
        const { background, hover, color } = cellColors(role);
        const muted = isEndpoint ? "inherit" : grey[400];
        const unit = isMonth ? "month" : "day";
        const isDisabled = !isPickable(date, unit);

        return (
          <ButtonBase
            key={`${isMonth}${date.toMillis()}`}
            aria-label={`${date.toFormat(isMonth ? "LLLL yyyy" : "yyyy-MM-dd")} ${captureLabel(count)}`}
            aria-pressed={role.isSelected}
            disabled={isDisabled}
            onClick={(event) => onPick(date, unit, event.shiftKey)}
            onPointerEnter={() => onHover?.([date, unit])}
            onPointerLeave={() => onHover?.()}
            onPointerDown={(event) => pressRow(event, [date, unit])}
            sx={{
              width: "100%",
              height: "32px",
              justifyContent: "flex-start",
              textAlign: "left",
              pr: 1.5,
              borderRadius: listRowRadius(role),
              color: isDisabled ? grey[600] : color,
              backgroundColor: background,
              "&:hover": { backgroundColor: hover },
            }}
          >
            <TimelineRail
              isMonth={isMonth}
              isFirst={index === 0}
              isLast={index === rows.length - 1}
              isSolid={isEndpoint}
            />
            {isMonth ? (
              <>
                <FaroText variant="bodyM" color="inherit">
                  {date.toLocaleString({ month: "short", year: "numeric" })}
                </FaroText>
                <FaroText variant="bodyS" color={muted} sx={{ ml: "auto" }}>
                  {captureLabel(count)}
                </FaroText>
              </>
            ) : (
              <>
                <FaroText
                  variant="bodyM"
                  color="inherit"
                  sx={{
                    width: LIST_DATE_WIDTH,
                    fontWeight: isEndpoint
                      ? FontWeights.SemiBold
                      : FontWeights.Regular,
                  }}
                >
                  {date.toLocaleString({ month: "short", day: "numeric" })}
                </FaroText>
                <FaroText variant="bodyM" color={muted}>
                  {date.toLocaleString({ weekday: "short" })}
                </FaroText>
                <Box component="span" sx={{ ml: "auto", display: "flex" }}>
                  <CaptureCountChip count={count} />
                </Box>
              </>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/**
 * @returns The corners of a row: rounded alone, or on the outer side of a range end so the
 * rows inside the range join into one block
 * @param role How the row relates to the active range
 */
function listRowRadius({
  isSelected,
  isRangeStart,
  isRangeEnd,
}: RangeRole): string {
  if (!isSelected) return LIST_ROW_RADIUS;
  const top = isRangeEnd ? LIST_ROW_RADIUS : "0";
  const bottom = isRangeStart ? LIST_ROW_RADIUS : "0";
  return `${top} ${top} ${bottom} ${bottom}`;
}
